import { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Rocket, Eye, MouseSimple, CaretDown, CaretRight, ArrowSquareOut } from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminSplash() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState({ pages: 0, users: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [pagesByUser, setPagesByUser] = useState({});
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/splash/users");
      setRows(data.users || []);
      setTotal({ pages: data.total_pages || 0, users: data.total_users || 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleUser = async (uid) => {
    setExpanded((s) => ({ ...s, [uid]: !s[uid] }));
    if (!pagesByUser[uid]) {
      try {
        const { data } = await api.get(`/admin/splash/users/${uid}/pages`);
        setPagesByUser((s) => ({ ...s, [uid]: data.pages || [] }));
      } catch (e) {
        toast.error(e.response?.data?.detail || e.message);
      }
    }
  };

  const filtered = q
    ? rows.filter((r) =>
        (r.username || "").toLowerCase().includes(q.toLowerCase()) ||
        (r.email || "").toLowerCase().includes(q.toLowerCase())
      )
    : rows;

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            Splash pages
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Every user who has created at least one splash page.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-4 py-2 text-sm">
            <span className="text-slate-500 dark:text-slate-400">Total pages</span>{" "}
            <span data-testid="admin-splash-total-pages" className="font-black text-slate-900 dark:text-white">{total.pages}</span>
          </div>
          <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-4 py-2 text-sm">
            <span className="text-slate-500 dark:text-slate-400">Creators</span>{" "}
            <span data-testid="admin-splash-total-users" className="font-black text-slate-900 dark:text-white">{total.users}</span>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <input
          data-testid="admin-splash-search"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by username or email…"
          className="w-full max-w-md rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
        />
      </div>

      <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left py-3 px-4 w-8"></th>
              <th className="text-left py-3 px-4">User</th>
              <th className="text-right py-3 px-4">Pages</th>
              <th className="text-right py-3 px-4">Views</th>
              <th className="text-right py-3 px-4">Clicks</th>
              <th className="text-left py-3 px-4">Last created</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="py-8 text-center text-slate-500">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-slate-500">No splash pages yet.</td></tr>
            )}
            {!loading && filtered.map((r) => (
              <Fragment key={r.user_id}>
                <tr
                  data-testid={`admin-splash-row-${r.user_id}`}
                  className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/60 dark:hover:bg-slate-800/40 cursor-pointer"
                  onClick={() => toggleUser(r.user_id)}
                >
                  <td className="py-3 px-4 text-slate-400">
                    {expanded[r.user_id] ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      {r.avatar_url && <img src={r.avatar_url} alt="" className="w-8 h-8 rounded-full bg-slate-100" />}
                      <div>
                        <div className="font-semibold text-slate-900 dark:text-white">{r.username}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{r.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right font-black text-slate-900 dark:text-white">{r.count}</td>
                  <td className="py-3 px-4 text-right text-slate-700 dark:text-slate-300">{r.views}</td>
                  <td className="py-3 px-4 text-right text-slate-700 dark:text-slate-300">{r.clicks}</td>
                  <td className="py-3 px-4 text-slate-600 dark:text-slate-400">{fmtDate(r.last_created)}</td>
                </tr>
                {expanded[r.user_id] && (
                  <tr key={`${r.user_id}-pages`} className="bg-slate-50/60 dark:bg-slate-800/30">
                    <td></td>
                    <td colSpan={5} className="py-3 px-4">
                      {!pagesByUser[r.user_id] && <div className="text-xs text-slate-500">Loading pages…</div>}
                      {pagesByUser[r.user_id] && pagesByUser[r.user_id].length === 0 && (
                        <div className="text-xs text-slate-500">No pages found.</div>
                      )}
                      {pagesByUser[r.user_id] && pagesByUser[r.user_id].length > 0 && (
                        <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                          {pagesByUser[r.user_id].map((p) => (
                            <li key={p.id} className="py-2 flex items-center justify-between gap-3 flex-wrap">
                              <div className="min-w-0">
                                <div className="font-semibold text-slate-800 dark:text-slate-100 truncate">{p.title || "(untitled)"}</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">/{p.slug} · created {fmtDate(p.created_at)}</div>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 shrink-0">
                                <span className="inline-flex items-center gap-1"><Eye size={14} /> {p.views || 0}</span>
                                <span className="inline-flex items-center gap-1"><MouseSimple size={14} /> {p.clicks || 0}</span>
                                <Link
                                  to={`/s/${p.slug}`}
                                  target="_blank"
                                  className="inline-flex items-center gap-1 text-lime-600 hover:text-lime-500 font-semibold"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Open <ArrowSquareOut size={12} weight="bold" />
                                </Link>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
